import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import EditIcon from '@mui/icons-material/Edit';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import MicIcon from '@mui/icons-material/Mic';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import TextFieldsIcon from '@mui/icons-material/TextFields';

import { CardContainer, CardDisplay, CardDisplayProps } from '@/components/displays/CardDisplay';
import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { WordPicker } from '@/components/inputs/WordPicker/WordPicker';
import { Popover } from '@/components/utils/Popover/Popover';
import { CANVAS_SIZE, RESULT_WRITING_V2_SIZE } from '@/constant';
import { Coordinate, GameType, Size, SlideComponentCharacterModel, SlideComponentCharacterSoundGroupModel, SlideComponentCharacterSoundModel, reposition } from '@/types/slide';
import { gql, useQuery } from '@apollo/client';
import { Box, Button, Divider, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSelector } from '../components/ImageSelector';
import { Pronunciation } from '../components/Pronunciation';

const shadowGlow = '0 0 4px 4px';

const NoAnswerCardDisplay = (props: CardDisplayProps) => {
    return (
        <CardDisplay sx={(theme) => ({ boxShadow: `${shadowGlow} ${(theme.vars || theme).palette.warning.light}`, borderRadius: 2 })} aria-label='no-answer-card' {...props}>
            <QuestionMarkIcon sx={(theme) => ({ width: 120, height: 120, color: (theme.vars || theme).palette.grey[800] })} />
        </CardDisplay>
    );
};

type GetCharacterResponse = {
    character: {
        id: string;
        cardImg1: string;
        cardImg2: string;
        cardType: number;
        character: string;
        serial1: string[];
        serial2: string[];
        code: string;
        locale: string;
    };
};

export const GET_CHARACTER = gql`
    query Character($where: CharacterWhereUniqueInput!) {
        character(where: $where) {
            id
            cardImg1
            cardImg2
            cardType
            character
            serial1
            serial2
            code
            locale
        }
    }
`;

const checkLegacyLogic = (soundLength: number, phraseLength: number) => {
    return soundLength > phraseLength && soundLength % phraseLength === 0;
};

const calculateRatio = (phraseSound: string, phrase: string) => {
    const wordCount = phraseSound.split(' ').length;
    const cleanedPhraseLength = phrase.replace(/[（）()]/g, '').length;
    return Math.floor(wordCount / cleanedPhraseLength);
};

const forceChangeGameType = (character: SlideComponentCharacterModel | undefined, gameType: string) => {
    // check hasCard
    if (isWritingOnlyType(character) && (gameType === 'OneCard' || gameType === 'TwoCards')) {
        return 'Writing';
    } else {
        return gameType as GameType;
    }
};

const isWritingOnlyType = (character: SlideComponentCharacterModel | undefined) => {
    if (!character) {
        return true;
    }

    if (character?.serial1?.length <= 0 && character?.serial2?.length <= 0) {
        return true;
    }

    return false;
};

type CharacterProps = {
    content: {
        image: string;
        characterList: SlideComponentCharacterModel[];
        character: number;
        characterSound: SlideComponentCharacterSoundModel;
        phrase: string;
        phraseSound: SlideComponentCharacterSoundModel;
        phraseSoundGroup?: SlideComponentCharacterSoundGroupModel;
        imageURL: string;
        gameType: GameType;
        flip: number;
        zIndex?: number;
        version?: number;
        normalMode?: boolean;
    };
    size: Size;
    pos: Coordinate;
};

export interface Props {
    anchorEl: HTMLElement | null;
    value: CharacterProps;
    onClose: () => void;
    onUpdate: (settings: CharacterProps) => void;
    onUpload?: (file: File) => Promise<string>;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
}

export const CharacterPicker: React.FC<Props> = ({ anchorEl, value, onClose, onUpdate, onUpload, slotProps }) => {
    const { t } = useTranslation();

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);
    const wordPickerRef = useRef<HTMLDivElement | null>(null);
    const wordSelectorRef = useRef<HTMLElement | null>(null);
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);

    // init values
    const open = Boolean(anchorEl);
    const character = value.content.characterList[value.content.character];
    const code = value.content.characterList[value.content.character]?.code;
    const { data: detail } = useQuery<GetCharacterResponse>(GET_CHARACTER, {
        variables: { where: { code: code } },
    });
    const direction = detail?.character.cardType === 2 ? 'column' : 'row';
    const locale = character?.locale ?? 'zh-HK';
    const serial1 = value.content.gameType === 'OneCard' ? character?.serial1[0] : undefined;
    const card1Orientation = direction === 'column' ? 'Right' : undefined;
    const img1 = `/asset/card/${locale}/${serial1}.png`;

    return (
        <>
            <Popover
                title={t('slide.legacy.character.title')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            display: 'flex',
                            p: 2,
                            width: CANVAS_SIZE.width,
                            height: CANVAS_SIZE.height,
                            overflow: 'scroll',
                        },
                    },
                }}
            >
                <Box sx={{ flexDirection: 'row', display: 'flex', flex: 1, gap: 2, width: CANVAS_SIZE.width - 32 }}>
                    <Box sx={{ display: 'flex' }}>
                        <Stack spacing={1} sx={{ alignItems: 'center', width: 300 }}>
                            <ImageSelector
                                src={value.content.imageURL ? value.content.imageURL : value.content.image ? `/asset/characters/${value.content.image}/image.png` : undefined}
                                onSelect={(_, selectorRef) => {
                                    setImagePickerAnchorEl(selectorRef.current);
                                }}
                            />
                            <Box sx={{ flex: 1, display: 'flex', width: '100%' }} ref={wordSelectorRef}>
                                <Stack
                                    spacing={1}
                                    sx={(theme) => ({
                                        padding: 1,
                                        borderRadius: 1,
                                        flex: 1,
                                        width: '100%',
                                        backgroundColor: (theme.vars || theme).palette.grey[200],
                                        ...theme.applyStyles('dark', {
                                            backgroundColor: (theme.vars || theme).palette.grey[600],
                                        }),
                                    })}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            flex: 1,
                                            borderRadius: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            p: 1,
                                            backgroundColor: (theme.vars || theme).palette.grey[100],
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: (theme.vars || theme).palette.grey[700],
                                            }),
                                            position: 'relative',
                                        })}
                                    >
                                        {value?.content?.phrase ? (
                                            <Box sx={{ flex: 1, alignContent: 'center' }}>
                                                <PhraseHighlight phrase={value.content.phrase} highlight={value.content.characterList[value.content.character]?.character} />
                                                <IconButton
                                                    sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                                    onClick={() => {
                                                        setWordPickerAnchorEl(wordSelectorRef.current);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <>
                                                <TextFieldsIcon sx={{ flex: 1, width: '60%', height: '60%', color: (theme) => (theme.vars || theme).palette.grey[400] }} />
                                                <Button
                                                    variant='contained'
                                                    onClick={() => {
                                                        setWordPickerAnchorEl(wordSelectorRef.current);
                                                    }}
                                                    fullWidth
                                                >
                                                    {t('slide.legacy.character.selectWord')}
                                                </Button>
                                            </>
                                        )}

                                        {value.content?.characterList && value.content?.characterList.length > 0 && (
                                            <Stack spacing={1} sx={{ width: '100%' }}>
                                                <Paper sx={{ p: 1 }}>
                                                    <Divider textAlign='left'>
                                                        <Typography variant='caption'>{t('slide.legacy.character.questionLabel')}</Typography>
                                                    </Divider>
                                                    <Stack direction={'row'} spacing={1} sx={{ width: '100%', mt: 1.5 }}>
                                                        <ToggleButtonGroup
                                                            exclusive
                                                            value={value.content?.characterList?.[value.content?.character]?.character}
                                                            onChange={(_event, newValue) => {
                                                                if (!newValue) {
                                                                    return;
                                                                }

                                                                const charIdx = value.content?.characterList?.findIndex((v) => v.character === newValue);
                                                                const char = value.content?.characterList?.[charIdx];

                                                                if (charIdx !== -1 && char) {
                                                                    const updatedContent = {
                                                                        ...value.content,
                                                                        character: charIdx,
                                                                        characterSound: { jyutping: char.jyutping, pinyin: char.pinyin },
                                                                        gameType: forceChangeGameType(char, value.content?.gameType),
                                                                    };
                                                                    const updateModel = { ...value, content: updatedContent };

                                                                    onUpdate?.(updateModel);
                                                                }
                                                            }}
                                                            sx={{
                                                                height: 40,
                                                            }}
                                                        >
                                                            {value.content?.phrase.split('').map((char) => {
                                                                return <ToggleButton value={char}>{char}</ToggleButton>;
                                                            })}
                                                        </ToggleButtonGroup>

                                                        <FormControl fullWidth>
                                                            <InputLabel id='game-type-select-label'>{t('slide.legacy.character.cardOrWriting')}</InputLabel>
                                                            <Select
                                                                labelId='game-type-select-label'
                                                                id='game-type-select-id'
                                                                value={value.content?.gameType}
                                                                label={t('slide.legacy.character.cardOrWriting')}
                                                                onChange={(event: SelectChangeEvent) => {
                                                                    const updatedContent = {
                                                                        ...value.content,
                                                                        gameType: forceChangeGameType(value.content?.characterList[value.content?.character], event.target.value),
                                                                    };
                                                                    const updateModel = { ...value, content: updatedContent };

                                                                    onUpdate?.(updateModel);
                                                                }}
                                                            >
                                                                <MenuItem value='OneCard' disabled={isWritingOnlyType(value.content?.characterList?.[value.content?.character])}>
                                                                    <Stack direction='row' spacing={1}>
                                                                        <InsertPhotoIcon sx={{ height: 20, width: 20 }} />
                                                                        <Typography>{t('slide.legacy.character.oneCard')}</Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                                <MenuItem value='TwoCards' disabled={isWritingOnlyType(value.content?.characterList?.[value.content?.character])}>
                                                                    <Stack direction='row' spacing={1}>
                                                                        <PhotoLibraryIcon sx={{ height: 20, width: 20 }} />
                                                                        <Typography>{t('slide.legacy.character.twoCards')}</Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                                <MenuItem value='Writing'>
                                                                    <Stack direction='row' spacing={1}>
                                                                        <AppRegistrationIcon sx={{ height: 20, width: 20 }} />
                                                                        <Typography>{t('slide.legacy.character.writing')}</Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                                <MenuItem value='Audio'>
                                                                    <Stack direction='row' spacing={1}>
                                                                        <MicIcon sx={{ height: 20, width: 20 }} />
                                                                        <Typography>{t('slide.legacy.character.audio')}</Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Stack>
                                                </Paper>
                                                <Paper sx={{ p: 1 }}>
                                                    <Divider textAlign='left'>
                                                        <Typography variant='caption'>{t('slide.legacy.character.choosePronunciation')}</Typography>
                                                    </Divider>
                                                    <Stack direction={'row'} spacing={1} sx={{ mt: 1.5 }}>
                                                        {value.content?.phraseSoundGroup && (
                                                            <FormControl fullWidth>
                                                                <InputLabel id='cantonese-pronunciation-select-label'>{t('common.cantonese')}</InputLabel>
                                                                <Select
                                                                    labelId='cantonese-pronunciation-select-label'
                                                                    id='cantonese-pronunciation-select-id'
                                                                    value={value.content?.phraseSoundGroup?.jyutping[0] ? value.content?.phraseSoundGroup.jyutping[0]?.startIndex.toString() : ''}
                                                                    label={t('common.cantonese')}
                                                                    onChange={(event: SelectChangeEvent) => {
                                                                        if (value.content?.phraseSoundGroup) {
                                                                            const phraseSoundGroup = {
                                                                                ...value.content?.phraseSoundGroup,
                                                                                ...(value.content?.phraseSoundGroup?.jyutping && {
                                                                                    jyutping: [{ startIndex: parseInt(event.target.value) }],
                                                                                }),
                                                                            };

                                                                            const updatedContent = {
                                                                                ...value.content,
                                                                                phraseSoundGroup: phraseSoundGroup,
                                                                            };
                                                                            const updateModel = { ...value, content: updatedContent };

                                                                            onUpdate?.(updateModel);
                                                                        }
                                                                    }}
                                                                >
                                                                    {value.content?.phraseSound?.jyutping?.map((phraseSound, index) => {
                                                                        const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, value.content?.phrase.length);

                                                                        if (isLegacyLogic) {
                                                                            return [...Array(calculateRatio(phraseSound.sound, value.content?.phrase))].map((_, idx) => (
                                                                                <MenuItem value={idx.toString()} key={idx}>
                                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                        <Pronunciation
                                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                                            phraseSound={value.content?.phraseSound}
                                                                                            phrase={value.content?.phrase}
                                                                                            groupIndex={idx}
                                                                                            folder={'jyutping'}
                                                                                        />
                                                                                        <Typography justifyContent='center' justifyItems={'center'} justifySelf={'center'} align='center'>
                                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                                i: idx + 1,
                                                                                            })}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                </MenuItem>
                                                                            ));
                                                                        } else {
                                                                            return (
                                                                                <MenuItem value={index.toString()} key={index}>
                                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                        <Pronunciation
                                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                                            phraseSound={value.content?.phraseSound}
                                                                                            phrase={value.content?.phrase}
                                                                                            groupIndex={index}
                                                                                            folder={'jyutping'}
                                                                                        />
                                                                                        <Typography>
                                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                                i: index + 1,
                                                                                            })}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                </MenuItem>
                                                                            );
                                                                        }
                                                                    })}
                                                                </Select>
                                                            </FormControl>
                                                        )}

                                                        {value.content?.phraseSoundGroup && (
                                                            <FormControl fullWidth>
                                                                <InputLabel id='putonghua-pronunciation-select-label'>{t('common.putonghua')}</InputLabel>
                                                                <Select
                                                                    labelId='putonghua-pronunciation-select-label'
                                                                    id='putonghua-pronunciation-select-id'
                                                                    value={value.content?.phraseSoundGroup?.pinyin && value.content?.phraseSoundGroup.pinyin[0]?.startIndex.toString()}
                                                                    label={t('common.putonghua')}
                                                                    onChange={(event: SelectChangeEvent) => {
                                                                        if (value.content?.phraseSoundGroup) {
                                                                            const phraseSoundGroup = {
                                                                                ...value.content?.phraseSoundGroup,
                                                                                ...(value.content?.phraseSoundGroup?.pinyin && {
                                                                                    pinyin: [{ startIndex: parseInt(event.target.value) }],
                                                                                }),
                                                                            };

                                                                            const updatedContent = {
                                                                                ...value.content,
                                                                                phraseSoundGroup: phraseSoundGroup,
                                                                            };
                                                                            const updateModel = { ...value, content: updatedContent };

                                                                            onUpdate?.(updateModel);
                                                                        }
                                                                    }}
                                                                >
                                                                    {value.content?.phraseSound?.pinyin?.map((phraseSound, index) => {
                                                                        const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, value.content?.phrase.length);

                                                                        if (isLegacyLogic) {
                                                                            return [...Array(calculateRatio(phraseSound.sound, value.content?.phrase))].map((_, idx) => (
                                                                                <MenuItem value={idx.toString()} key={idx}>
                                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                        <Pronunciation
                                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                                            phraseSound={value.content?.phraseSound}
                                                                                            phrase={value.content?.phrase}
                                                                                            groupIndex={idx}
                                                                                            folder={'pinyin'}
                                                                                        />
                                                                                        <Typography>
                                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                                i: idx + 1,
                                                                                            })}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                </MenuItem>
                                                                            ));
                                                                        } else {
                                                                            return (
                                                                                <MenuItem value={index.toString()} key={index}>
                                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                        <Pronunciation
                                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                                            phraseSound={value.content?.phraseSound}
                                                                                            phrase={value.content?.phrase}
                                                                                            groupIndex={index}
                                                                                            folder={'pinyin'}
                                                                                        />
                                                                                        <Typography>
                                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                                i: index + 1,
                                                                                            })}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                </MenuItem>
                                                                            );
                                                                        }
                                                                    })}
                                                                </Select>
                                                            </FormControl>
                                                        )}
                                                    </Stack>
                                                </Paper>
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {(value.content?.gameType === 'OneCard' || value.content?.gameType === 'TwoCards') && (
                            <Box sx={{ height: 480, width: 480 }}>
                                <Stack spacing={1.5} direction={direction} sx={{ height: '100%', width: '100%' }}>
                                    <CardContainer>
                                        {serial1 ? (
                                            <CardDisplay direction={direction} orientation={card1Orientation} imgUrl={img1} aria-label='card1' sx={{ borderRadius: 2 }} />
                                        ) : (
                                            <NoAnswerCardDisplay direction={direction} />
                                        )}
                                    </CardContainer>
                                    <CardContainer>
                                        <NoAnswerCardDisplay direction={direction} />
                                    </CardContainer>
                                </Stack>
                            </Box>
                        )}
                        {value.content?.gameType === 'Writing' && (
                            <Box
                                sx={(theme) => ({
                                    width: RESULT_WRITING_V2_SIZE.width,
                                    height: RESULT_WRITING_V2_SIZE.height,
                                    borderRadius: 3,
                                    outline: 4,
                                    outlineColor: 'divider',
                                    backgroundColor: (theme.vars || theme).palette.grey[50],
                                })}
                            >
                                <Box
                                    sx={{
                                        background: `url(/asset/svg/${character?.code}_${character?.character}/${character?.character}.svg)`,
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0.15,
                                    }}
                                />
                            </Box>
                        )}
                        {value.content?.gameType === 'Audio' && (
                            <Box
                                sx={(theme) => ({
                                    borderRadius: 120,
                                    width: 120,
                                    aspectRatio: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: (theme.vars || theme).palette.grey[100],
                                    ...theme.applyStyles('dark', {
                                        backgroundColor: (theme.vars || theme).palette.grey[600],
                                    }),
                                })}
                            >
                                <KeyboardVoiceIcon
                                    sx={{
                                        width: 80,
                                        height: 80,
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Popover>

            <ImagePicker
                type='image'
                title={t('slide.legacy.character.title')}
                librarySources={BUILTIN_IMG_SRC}
                anchorEl={imagePickerAnchorEl}
                slotProps={{
                    paper: {
                        ref: imagePickerRef, // Attach the ref to the Paper element
                    },
                }}
                value={{
                    value: value?.content?.image,
                    imageURL: value?.content?.imageURL,
                }}
                onClose={() => {
                    setImagePickerAnchorEl(null);
                }}
                onUpdate={(settings) => {
                    const updatedContent = { ...value.content, image: settings.value, imageURL: settings.imageURL };

                    let updateModel = { ...value, content: updatedContent, ...(settings.size ? { size: settings.size } : {}) };
                    updateModel = reposition(updateModel);

                    onUpdate?.(updateModel);
                }}
                onUpload={onUpload}
            />

            <WordPicker
                type='phrase'
                title={t('slide.legacy.character.characterSearch')}
                anchorEl={wordPickerAnchorEl}
                slotProps={{
                    paper: {
                        ref: wordPickerRef, // Attach the ref to the Paper element
                    },
                }}
                value={{
                    characterList: value?.content?.characterList,
                    characterSound: value?.content?.characterSound,
                    phrase: value?.content?.phrase,
                    phraseSound: value?.content?.phraseSound,
                    phraseSoundGroup: value?.content?.phraseSoundGroup,
                }}
                onClose={() => {
                    setWordPickerAnchorEl(null);
                }}
                onUpdate={(settings) => {
                    if (!settings.characterList || !settings.characterSound || !settings.phraseSound || !settings.phraseSoundGroup) {
                        return;
                    }

                    const updatedContent = {
                        ...value.content,
                        character: 0, // reset character index
                        characterList: settings.characterList,
                        characterSound: settings.characterSound,
                        phrase: settings.phrase,
                        phraseSound: settings.phraseSound,
                        phraseSoundGroup: settings.phraseSoundGroup,
                    };

                    if (isWritingOnlyType(updatedContent.characterList?.[updatedContent.character]) && (updatedContent.gameType === 'OneCard' || updatedContent.gameType === 'TwoCards')) {
                        // If the game type is OneCard or TwoCards, set it to Writing
                        updatedContent.gameType = 'Writing';
                    }

                    const updateModel = { ...value, content: updatedContent };

                    onUpdate?.(updateModel);
                }}
            />
        </>
    );
};
